import t from 'react-translate';
import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { blue05, novoEdBlue } from 'athena/styles/colors';
import {
  gray1,
  gray2,
  gray3,
  gray4,
  gray6,
  gray7,
  hexToRgbaString,
  highTide,
  primary,
} from 'styles/global_defaults/colors';
import {
  semiBoldFontWeight,
  textLargeBodyFontSize,
} from 'styles/global_defaults/fonts';
import { halfSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { formatSessionTime } from 'athena/helpers/utils';
import { TimeZoneData } from 'athena/hooks/use-time-zone';

type SessionCardProps = {
  title: string;
  time: string;
  isPulseCheckResponded: boolean;
  duration: number;
  timeZone: TimeZoneData;
  planId?: number;
};

const SessionCard = ({
  title,
  time,
  duration,
  timeZone,
  planId,
  isPulseCheckResponded,
}: SessionCardProps) => {
  const isPast = moment().isAfter(moment(time).add(duration || 0, 'minutes'));

  const [hasOverflow, setHasOverflow] = useState(false);
  const titleRef = useRef(null);
  useEffect(() => {
    setHasOverflow(
      titleRef.current?.scrollHeight > titleRef.current?.clientHeight,
    );
  }, [titleRef]);

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { pulseChecksEnabled } = mentoringProgram || {};

  const styles = css`
    display: flex;
    width: 100%;
    border: 1px solid ${gray6};
    border-radius: ${quarterSpacing}px;

    &:hover {
      box-shadow: 0px 2px 4px 0px ${hexToRgbaString(gray1, 0.08)};

      .session-title {
        color: ${primary};
      }
    }

    .tab {
      width: ${quarterSpacing}px;
      background-color: ${isPast ? gray3 : novoEdBlue};
      border-top-left-radius: ${quarterSpacing}px;
      border-bottom-left-radius: ${quarterSpacing}px;
    }

    .session-info {
      width: calc(100% - ${quarterSpacing}px);
    }

    .icon-calendar {
      color: ${isPast ? gray2 : novoEdBlue};
      background-color: ${isPast ? gray7 : blue05};
      padding: ${quarterSpacing}px;
      border-radius: 13px; // This is height of icon (16) + padding*2 (5*2), all divided by 2 => 13
      margin-right: ${quarterSpacing}px;
    }

    .session-time {
      color: ${gray2};
      .recurring-icon {
        color: ${gray4};
      }
    }

    .session-title {
      font-weight: ${semiBoldFontWeight};
      font-size: ${textLargeBodyFontSize}px;

      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
    }

    .pending-feedback {
      height: 25px;
      padding: ${quarterSpacing}px ${halfSpacing}px;
      color: ${highTide};
      text-wrap: nowrap;
      background-color: ${blue05};
      border-radius: 100px;
    }
  `;

  return (
    <div css={styles}>
      <div className='tab' />
      <div className='session-info p-4'>
        <div className='d-flex justify-content-between'>
          <div className='d-flex align-items-center'>
            <NvIcon icon='calendar' size='smallest' />
            <div className='d-flex session-time align-items-center'>
              {formatSessionTime(time, duration, timeZone)}
              {!!planId && (
                <NvIcon icon='recurring' size='smallest' className='pl-2 recurring-icon' />
              )}
            </div>
          </div>
          {pulseChecksEnabled && isPast && !isPulseCheckResponded && (
            <div className='pending-feedback semi-bold align-content-center text-small'>
              {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.SESSIONS.PENDING_FEEDBACK()}
            </div>
          )}
        </div>
        <NvTooltip enabled={hasOverflow} text={title} preventOverflow={false}>
          <div className='session-title' ref={titleRef}>
            {title}
          </div>
        </NvTooltip>
      </div>
    </div>
  );
};

export default SessionCard;
